import { models, stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IEntity } from '@kurtosys/types/api/search/IEntity';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IQueryKeyValue } from '@kurtosys/types/query/IQueryKeyValue';
import { action, computed, makeObservable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { IConfigurationRedirect } from '../../../models/app/IConfigurationRedirect.js';
import { IInputs } from '../../../models/app/IInputs.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { DropdownStore } from '../../Dropdown/stores/DropdownStore.js';
import { SearchStore } from '../../Search/stores/SearchStore.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';

/* [Component: appStoreComponentImport] */

const Query = query.Query;

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

const { RedirectHelper } = common.helpers;
const { deepMergeObjects } = common.commonUtils;

const { convertQueryKeyValue } = query.queryUtils.queryHelper;

type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	url: URL;
	rawUrl: string;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.rawUrl = url;
		this.url = new URL(url);

		makeObservable(this);
	}

	@computed
	get searchStore(): SearchStore {
		return this.storeContext.get<SearchStore>('searchStore');
	}

	@computed
	get dropdownStore(): DropdownStore {
		return this.storeContext.get<DropdownStore>('dropdownStore');
	}

	@computed
	get hasData(): boolean {
		return true;
	}

	async customInitializeAfter() {
		// These should be async and the app component should not wait for them to load
		this.searchStore.initialize();
		this.dropdownStore.initialize();
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get gridProps(): components.base.Grid.models.IGridProps | undefined {
		const gridProps = this.appComponentConfiguration && this.appComponentConfiguration.gridProps;
		return gridProps;
	}

	get redirectConfiguration(): IConfigurationRedirect | undefined {
		return this.configuration?.redirect;
	}

	redirect(item: IEntity, analyticsContext?: Record<string, any>) {
		if (item) {
			const redirectHelper = new RedirectHelper({ queryClass: query.Query }, this.rawUrl);
			const redirectConfiguration = this.redirectConfiguration;
			if (redirectConfiguration) {
				const { redirect, inputs: redirectInputs } = redirectConfiguration;
				const inputQueriesByKey = redirectInputs.reduce((redirectConfigInputs, inputConfig) => {
					if (!redirectConfigInputs[inputConfig.key]) {
						redirectConfigInputs[inputConfig.key] = inputConfig.query;
					}
					return redirectConfigInputs;
				}, {} as IQueryKeyValue);
				const { type } = item;
				const { context, ...existingExecutionOptions } = this.queryStore.executionOptions;
				const executionOptions = deepMergeObjects(existingExecutionOptions, {
					context: {
						entityByType: {
							[type]: item,
						},
					},
				});
				const inputs = convertQueryKeyValue(
					{ queryClass: query.Query },
					inputQueriesByKey || {},
					executionOptions,
				);
				if (analyticsContext && this.analyticsHelper) {
					this.analyticsHelper.logEvent({
						event: 'button_click',
						eventType: 'click',
						context: analyticsContext,
					});
				}
				redirectHelper.go(redirect, inputs);
			}
		}
	}

	getInput(inputKey: keyof IInputs) {
		return this.appParamsHelper.inputs && this.appParamsHelper.inputs[inputKey];
	}

	overrideEntityContext(entity: IEntity) {
		return { context: { entityByType: { [entity.type]: entity } } };
	}
}
